import { db, isFirebaseConfigured } from '../config/firebase';
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  writeBatch,
} from 'firebase/firestore';

const slugify = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

const normalizeCollection = (c, index) => {
  const slug = slugify(c?.slug || c?.id || c?.name);
  return {
    id: slug,
    slug,
    name: String(c?.name || slug).toUpperCase(),
    description: String(c?.description || ''),
    heroImage: String(c?.heroImage || ''),
    productIds: Array.isArray(c?.productIds) ? c.productIds : [],
    visible: c?.visible !== false,
    order: Number.isFinite(Number(c?.order)) ? Number(c.order) : index,
    createdAt: c?.createdAt || new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
};

const emitChanged = () => {
  try {
    window.dispatchEvent(new Event('ditris:collections'));
  } catch {
    // ignore
  }
};

let cachedCollections = [];
let started = false;
let unsub = null;

const startSync = () => {
  if (started) return;
  started = true;
  if (!isFirebaseConfigured || !db) return;

  try {
    const colRef = collection(db, 'collections');
    const q = query(colRef, orderBy('order', 'asc'));
    unsub = onSnapshot(q, (snap) => {
      try {
        cachedCollections = snap.docs.map((d, i) => normalizeCollection({ id: d.id, ...d.data() }, i));
        emitChanged();
      } catch {
        // ignore
      }
    });
  } catch {
    // ignore
  }
};

const readAll = () => {
  startSync();
  const normalized = (cachedCollections || []).map((c, i) => normalizeCollection(c, i));
  normalized.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  return normalized;
};

export const getCollections = ({ includeHidden = false } = {}) => {
  const items = readAll();
  return includeHidden ? items : items.filter((c) => c.visible !== false);
};

export const upsertCollection = async ({ name, slug, description = '', heroImage = '', productIds = [], visible = true }) => {
  const normalizedSlug = slugify(slug || name);
  if (!normalizedSlug) {
    throw new Error('Invalid collection slug');
  }

  const items = readAll();
  const existingIndex = items.findIndex((c) => c.slug === normalizedSlug);
  const now = new Date().toISOString();

  if (existingIndex === -1) {
    const nextOrder = items.length > 0 ? Math.max(...items.map((c) => Number(c.order || 0))) + 1 : 0;
    const created = {
      id: normalizedSlug,
      slug: normalizedSlug,
      name: String(name || normalizedSlug).toUpperCase(),
      description: String(description || ''),
      heroImage: String(heroImage || ''),
      productIds: Array.isArray(productIds) ? productIds : [],
      visible: visible !== false,
      order: nextOrder,
      createdAt: now,
      updatedAt: now,
    };
    cachedCollections = [...items, created];
    emitChanged();

    if (isFirebaseConfigured && db) {
      await setDoc(
        doc(db, 'collections', normalizedSlug),
        { ...created, createdAt: serverTimestamp(), updatedAt: serverTimestamp() },
        { merge: true }
      );
    }
    return created;
  }

  const updated = {
    ...items[existingIndex],
    name: String(name || items[existingIndex].name || normalizedSlug).toUpperCase(),
    description: String(description || ''),
    heroImage: String(heroImage || ''),
    productIds: Array.isArray(productIds) ? productIds : [],
    visible: visible !== false,
    updatedAt: now,
  };

  const next = items.slice();
  next[existingIndex] = updated;
  cachedCollections = next;
  emitChanged();

  if (isFirebaseConfigured && db) {
    await setDoc(
      doc(db, 'collections', normalizedSlug),
      { ...updated, updatedAt: serverTimestamp() },
      { merge: true }
    );
  }
  return updated;
};

export const setCollectionVisibility = async (slug, visible) => {
  const s = slugify(slug);
  const items = readAll();
  const idx = items.findIndex((c) => c.slug === s);
  if (idx === -1) return;
  const next = items.slice();
  next[idx] = { ...next[idx], visible: visible !== false, updatedAt: new Date().toISOString() };
  cachedCollections = next;
  emitChanged();

  if (isFirebaseConfigured && db) {
    await setDoc(
      doc(db, 'collections', s),
      { visible: visible !== false, updatedAt: serverTimestamp() },
      { merge: true }
    );
  }
};

export const moveCollection = async (slug, direction) => {
  const s = slugify(slug);
  const items = readAll();
  const idx = items.findIndex((c) => c.slug === s);
  if (idx === -1) return;

  const next = items.slice();
  const swapWith = direction === 'up' ? idx - 1 : idx + 1;
  if (swapWith < 0 || swapWith >= next.length) return;

  const a = next[idx];
  const b = next[swapWith];
  next[idx] = { ...b, order: a.order, updatedAt: new Date().toISOString() };
  next[swapWith] = { ...a, order: b.order, updatedAt: new Date().toISOString() };
  cachedCollections = next;
  emitChanged();

  if (isFirebaseConfigured && db) {
    const batch = writeBatch(db);
    batch.set(doc(db, 'collections', next[idx].slug), { order: next[idx].order, updatedAt: serverTimestamp() }, { merge: true });
    batch.set(
      doc(db, 'collections', next[swapWith].slug),
      { order: next[swapWith].order, updatedAt: serverTimestamp() },
      { merge: true }
    );
    await batch.commit();
  }
};

export const deleteCollection = async (slug) => {
  const s = slugify(slug);
  if (!s) return;
  const items = readAll();
  const next = items.filter((c) => c.slug !== s);
  cachedCollections = next;
  emitChanged();

  if (isFirebaseConfigured && db) {
    await deleteDoc(doc(db, 'collections', s));
  }
};
